import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Ajax from '@/utils/Ajax';
import Currencies from '@/utils/Currencies';
import _ from 'lodash';
import classNames from 'classnames';

const COUPON_STATUSES = {
    IDLE: 'idle',
    LOADING: 'loading',
    SUCCESS: 'success',
    ERROR: 'error',
};

const DISCOUNT_TYPES = {
    PERCENT: 'percent',
    AMOUNT: 'amount',
    NONE: 'none',
};

const normalizeCouponResponse = (res, basePriceMinor) => {
    const eligible =
        res?.eligible === true ||
        (Array.isArray(res?.redeemables) &&
            res.redeemables.some((r) => String(r?.status || '').toUpperCase() === 'APPLICABLE'));

    // Prefer normalized shape from server
    let discount = res?.discount || null;

    // Fallback to raw Voucherify-like structure
    if (!discount && res?.redeemables?.[0]?.voucher?.discount) {
        const d = res.redeemables[0].voucher.discount;
        const t = String(d.type || '').toUpperCase();
        if (t === 'PERCENT') {
            const percent = d.percent_off || d.value || 0;
            const amount = Number.isFinite(basePriceMinor) ? Math.round((basePriceMinor || 0) * (percent / 100)) : 0;
            discount = { type: DISCOUNT_TYPES.PERCENT, percent, amount };
        } else if (t === 'AMOUNT') {
            discount = { type: DISCOUNT_TYPES.AMOUNT, amount: d.amount_off || d.amount || 0, percent: 0 };
        } else {
            discount = { type: DISCOUNT_TYPES.NONE, amount: 0, percent: 0 };
        }
    }

    return { eligible, discount };
};

const CouponCodeComponent = ({ reservation, isFormLocked, onValidated = () => {} }) => {
    const { t } = useTranslation();
    const [coupon, setCoupon] = useState('');
    const [status, setStatus] = useState(COUPON_STATUSES.IDLE);
    const [discountText, setDiscountText] = useState('');

    const handleApply = async () => {
        if (!coupon) return;
        setStatus(COUPON_STATUSES.LOADING);

        try {
            const payload = {
                reservation_id: _.get(reservation, 'id'),
                code: coupon.trim(),
            };
            const res = await Ajax.validateCoupon(payload);
            const { eligible, discount } = normalizeCouponResponse(res, _.get(payload, 'cart.base_price'));
            if (eligible) {
                let displayLabel = '';
                if (discount) {
                    if (discount.type === DISCOUNT_TYPES.PERCENT && Number.isFinite(discount.percent)) {
                        displayLabel = `${discount.percent}%`;
                    } else if (
                        discount.type === DISCOUNT_TYPES.AMOUNT &&
                        Number.isFinite(discount.amount) &&
                        discount.amount > 0
                    ) {
                        const symbol = Currencies.getSymbol(reservation?.deal?.currency);
                        const amountMajor = Math.max(0, (discount.amount || 0) / 100);
                        displayLabel = `${symbol}${amountMajor.toLocaleString()}`;
                    }
                }
                setDiscountText(displayLabel);
                setStatus(COUPON_STATUSES.SUCCESS);
            } else {
                setDiscountText('');
                setStatus(COUPON_STATUSES.ERROR);
            }
            onValidated({
                code,
                eligible,
                discount,
            });
        } catch (e) {
            setDiscountText('');
            setStatus(COUPON_STATUSES.ERROR);
            onValidated({
                code: coupon.trim(),
                eligible: false,
                discount: null,
            });
        }
    };

    const handleRemove = () => {
        setCoupon('');
        setStatus(COUPON_STATUSES.IDLE);
        setDiscountText('');
        onValidated(null);
    };

    return (
        <div
            className={`coupon-code ${status !== COUPON_STATUSES.IDLE ? 'coupon-code--active' : ''} ${isFormLocked ? 'disabled' : ''}`}
        >
            {status === COUPON_STATUSES.SUCCESS ? (
                <div className="coupon-code__success">
                    <div className="coupon-code__icon">✔</div>
                    <div className="coupon-code__text">
                        <div className="coupon-code__title">{t('CouponCodeAccepted')}</div>
                        <div className="coupon-code__subtitle">
                            {t('CouponCodeSuccessMessage', { discount: discountText || ' ' })}
                        </div>
                    </div>
                    <button className="coupon-code__remove" onClick={handleRemove}>
                        {t('Remove')}
                    </button>
                </div>
            ) : (
                <>
                    <label htmlFor="coupon-code-input" className="coupon-code__label">
                        {t('HaveACoupon')}
                    </label>

                    <div className="coupon-code__controls">
                        <input
                            id="coupon-code-input"
                            name="coupon-code-input"
                            type="text"
                            value={coupon}
                            onChange={(e) => setCoupon(e.target.value)}
                            placeholder={t('EnterYourCodeHere')}
                            className={`coupon-code__input ${
                                status === COUPON_STATUSES.ERROR ? 'coupon-code__input--error' : ''
                            }`}
                            disabled={isFormLocked}
                        />

                        <button
                            onClick={handleApply}
                            className={classNames('coupon-code__apply', {
                                'coupon-loading': status === COUPON_STATUSES.LOADING,
                                disabled: isFormLocked,
                            })}
                            disabled={status === COUPON_STATUSES.LOADING || isFormLocked}
                        >
                            {status === COUPON_STATUSES.LOADING ? <div className="spinner" /> : t('Apply')}
                        </button>

                        {status !== COUPON_STATUSES.IDLE && (
                            <button className="coupon-code__remove" onClick={handleRemove}>
                                {t('Remove')}
                            </button>
                        )}
                    </div>

                    {status === COUPON_STATUSES.ERROR && (
                        <div className="coupon-code__error">{t('CouponCodeErrorMessage')}</div>
                    )}
                </>
            )}
        </div>
    );
};

export default CouponCodeComponent;
